import type { TextBlock } from "@prismicio/types-internal/lib/content"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import { mergeAttributes } from "@tiptap/core"
import TTCodeBlock from "@tiptap/extension-code-block"
import type { Node as ProsemirrorNode, Schema } from "prosemirror-model"

import { Text } from "../../coreExtensions"
import { converter as TextDirection } from "../../globalExtensions/TextDirection"
import { TextExtension } from "../../models"

const component = TTCodeBlock.extend({
  name: "preformatted",
  addAttributes() {
    return {
      language: {
        default: null,
        parseHTML: (element) => {
          const { languageClassPrefix } = this.options
          const classNames = [...(element.firstElementChild?.classList || [])]
          const languages = classNames
            .filter((className) => className.startsWith(languageClassPrefix))
            .map((className) => className.replace(languageClassPrefix, ""))
          const language = languages[0]

          if (!language) {
            return null
          }

          return language
        },
        renderHTML: (attributes) => {
          if (!attributes["language"]) {
            return null
          }

          return {
            class:
              this.options.languageClassPrefix +
              (attributes["language"] as string),
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: "pre",
        preserveWhitespace: "full",
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "pre",
      mergeAttributes(this.options.HTMLAttributes),
      ["code", HTMLAttributes, 0],
    ]
  },
}).configure({
  exitOnTripleEnter: false,
})

const converter = {
  fromPrismic(
    schema: Schema,
  ): (codeBlock: TextBlock) => ProsemirrorNode | undefined {
    return function (codeBlock: TextBlock): ProsemirrorNode | undefined {
      const content = Text.converter.fromPrismic(schema)(
        component.name,
        codeBlock,
        /* preventHardbreaks */ true,
      )
      return schema.nodes[component.name]?.create(
        TextDirection.fromPrismic(codeBlock),
        content,
      )
    }
  },
  toPrismic(node: ProsemirrorNode): TextBlock | undefined {
    return Text.converter.toPrismic(
      node,
      RichTextNodeType.preformatted,
      TextDirection.toPrismic(node),
    )
  },
}

const EditorCodeBlock = new TextExtension(
  [RichTextNodeType.preformatted],
  component.name,
  component,
  converter,
  /* placeholder */ "",
  /* locked format */ true,
)

// eslint-disable-next-line import/no-default-export
export default EditorCodeBlock
